import { encrypt, decrypt } from 'utils/cryptoUtils';
import { CACHE_KEYS, URL_PARAM_KEYS } from 'utils/constants';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const NUMERIC_ALIAS_PATTERN = /^[1-9]\d*$/;

function isUuid(value: string): boolean {
  return UUID_REGEX.test(value);
}

function parseNumericAlias(alias: string): number | null {
  const normalized = alias.trim();
  if (!NUMERIC_ALIAS_PATTERN.test(normalized)) {
    return null;
  }
  return Number.parseInt(normalized, 10);
}

export async function getActiveCompanyIdFromStorage(): Promise<string | null> {
  const encryptedCompanyId = localStorage.getItem(CACHE_KEYS.ACTIVE_COMPANY_ID);
  if (!encryptedCompanyId) return null;
  try {
    return await decrypt(encryptedCompanyId);
  } catch {
    localStorage.removeItem(CACHE_KEYS.ACTIVE_COMPANY_ID);
    return null;
  }
}

export async function setActiveCompanyIdInStorage(
  companyId: string,
): Promise<void> {
  const encrypted = await encrypt(companyId);
  localStorage.setItem(CACHE_KEYS.ACTIVE_COMPANY_ID, encrypted);
}

interface CompanyAliasMap {
  byAlias: Record<string, string>;
  byCompanyId: Record<string, string>;
}

const EMPTY_ALIAS_MAP: CompanyAliasMap = {
  byAlias: {},
  byCompanyId: {},
};

function isValidAliasMap(value: unknown): value is CompanyAliasMap {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<CompanyAliasMap>;
  return (
    !!candidate.byAlias &&
    typeof candidate.byAlias === 'object' &&
    !!candidate.byCompanyId &&
    typeof candidate.byCompanyId === 'object'
  );
}

function getCompanyAliasMapFromStorage(): CompanyAliasMap {
  const raw = localStorage.getItem(CACHE_KEYS.ACTIVE_COMPANY_ALIAS_MAP);
  if (!raw) return EMPTY_ALIAS_MAP;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isValidAliasMap(parsed)) {
      localStorage.removeItem(CACHE_KEYS.ACTIVE_COMPANY_ALIAS_MAP);
      return EMPTY_ALIAS_MAP;
    }
    return {
      byAlias: { ...parsed.byAlias },
      byCompanyId: { ...parsed.byCompanyId },
    };
  } catch {
    localStorage.removeItem(CACHE_KEYS.ACTIVE_COMPANY_ALIAS_MAP);
    return EMPTY_ALIAS_MAP;
  }
}

function getLegacyCompanyIdByAlias(alias: string): string | null {
  const normalizedAlias = alias.trim();
  if (!normalizedAlias) return null;
  const aliasMap = getCompanyAliasMapFromStorage();
  return aliasMap.byAlias[normalizedAlias] ?? null;
}

/**
 * Resolves `co` to a company id.
 *
 * Prefer stable company UUID in the URL. Still accepts legacy 1-based list
 * indexes (`1`, `2`, …) and random aliases from localStorage for old bookmarks.
 */
export function getCompanyIdByAlias(
  alias: string,
  orderedCompanyIds: readonly string[],
): string | null {
  const normalized = alias.trim();
  if (!normalized) return null;

  if (orderedCompanyIds.includes(normalized)) {
    return normalized;
  }

  // Unknown UUID should not fall through to numeric/legacy parsing.
  if (isUuid(normalized)) {
    return null;
  }

  const numericIndex = parseNumericAlias(normalized);
  if (numericIndex !== null) {
    return orderedCompanyIds[numericIndex - 1] ?? null;
  }

  return getLegacyCompanyIdByAlias(normalized);
}

/**
 * Resolves company id from a location `search` string:
 * 1. `companyId` — explicit UUID (e.g. deep links from notification emails)
 * 2. `co` — company UUID (preferred), or legacy 1-based index / local alias
 */
export function getCompanyIdFromLocationSearch(
  search: string,
  orderedCompanyIds: readonly string[] = [],
): string | null {
  const params = new URLSearchParams(
    search.startsWith('?') ? search : `?${search}`,
  );
  const companyIdParam = params.get('companyId')?.trim() ?? '';
  if (companyIdParam && isUuid(companyIdParam)) {
    return companyIdParam;
  }
  const aliasParamKey = URL_PARAM_KEYS?.COMPANY_ALIAS ?? 'co';
  const alias = params.get(aliasParamKey)?.trim() ?? '';
  if (!alias) return null;
  return getCompanyIdByAlias(alias, orderedCompanyIds);
}

/**
 * Stable `co` value for a company: the company id itself (UUID).
 * Returns null when the company is not in the user's current company list.
 */
export function getAliasForCompanyId(
  companyId: string,
  orderedCompanyIds: readonly string[],
): string | null {
  const normalizedCompanyId = companyId.trim();
  if (!normalizedCompanyId) return null;
  if (!orderedCompanyIds.includes(normalizedCompanyId)) return null;
  return normalizedCompanyId;
}

export function getAliasByCompanyId(
  companyId: string,
  orderedCompanyIds: readonly string[],
): string | null {
  return getAliasForCompanyId(companyId, orderedCompanyIds);
}

export function ensureAliasForCompanyId(
  companyId: string,
  orderedCompanyIds: readonly string[],
): string {
  const alias = getAliasForCompanyId(companyId, orderedCompanyIds);
  if (!alias) {
    throw new Error('Company ID is required to create alias');
  }
  return alias;
}
